import React from 'react';
import { message } from 'antd';
import { useForm } from '@refinedev/antd';
import { type BaseRecord } from '@refinedev/core';
import { FormDisplayMode, SmartDataTableProps } from '../types';
import { CommonForm } from '../../shared/forms/CommonForm';

interface EditComponentProps {
  api: SmartDataTableProps['api'];
  editForm: SmartDataTableProps['editForm'];
  formDisplayMode: FormDisplayMode;
  visible: boolean;
  selectedRecord: BaseRecord | null;
  onClose: () => void;
  onSuccess?: (data: any) => void;
  rowKey?: string;
  drawerWidth?: number | string;
  modalWidth?: number | string;
  // Chat configuration
  enableChat?: boolean;
  chatType?: string;
  chatRelativeId?: number;
  chatPatientId?: number;
  chatTitle?: string;
  chatPlaceholder?: string;
  // Custom title component
  customTitle?: React.ReactNode;
  // Auto-scroll configuration
  isintialScroll?: boolean;
}

export const EditComponent: React.FC<EditComponentProps> = ({
  api,
  editForm,
  formDisplayMode,
  visible,
  selectedRecord,
  onClose,
  onSuccess,
  rowKey = 'id',
  drawerWidth,
  modalWidth,
  enableChat,
  chatType,
  chatRelativeId,
  chatPatientId,
  chatTitle,
  chatPlaceholder,
  customTitle,
  isintialScroll,
}) => {
  // Use useForm without fetching data since we have selectedRecord
  const formHookResult = useForm({
    resource: api.resource,
    id: selectedRecord?.[rowKey],
    action: 'edit',
    redirect: false,
    queryOptions: {
      enabled: false,
    },
    onMutationSuccess: data => {
      message.success('Record updated successfully!');
      onClose();
      onSuccess?.(data);
    },
    onMutationError: error => {
      message.error(
        error?.response?.data?.message ||
          'Failed to update record. Please check your input and try again.'
      );
    },
  });

  const {
    form,
    formProps: editFormProps,
    saveButtonProps: editSaveButtonProps,
    formLoading,
  } = formHookResult;

  // Determine if we should render the form
  const shouldRenderForm = editForm && visible && !!selectedRecord;

  // Helper function to check if a field is a mobile number field
  const isMobileNumberField = (fieldName: string): boolean => {
    const mobileFieldPatterns = [
      'mobile_no',
      'mobile',
      'phone',
      'patient_mobile_no',
      'customer_phone',
    ];
    return mobileFieldPatterns.some(pattern =>
      fieldName.toLowerCase().includes(pattern.toLowerCase())
    );
  };

  // Transform data for editing - remove '91' prefix from mobile numbers
  const transformForEditing = (data: any): any => {
    if (!data || typeof data !== 'object') return data;

    const transformedData = { ...data };

    Object.keys(transformedData).forEach(key => {
      const value = transformedData[key];
      if (
        isMobileNumberField(key) &&
        typeof value === 'string' &&
        value.startsWith('91') &&
        value.length === 12
      ) {
        transformedData[key] = value.substring(2); // Remove '91' prefix
      }

      // Transform patient type field from comma-separated string to array
      if (key === 'type' && typeof value === 'string' && value.includes(',')) {
        transformedData[key] = value
          .split(',')
          .map((type: string) => type.trim());
      }

      // Transform body part field from comma-separated string to array
      if (
        key === 'body_part' &&
        typeof value === 'string' &&
        value.includes(',')
      ) {
        transformedData[key] = value
          .split(',')
          .map((part: string) => part.trim())
          .filter(Boolean);
      }

      // Treatment names: comma-separated or single value → tags array (like body_part)
      if (key === 'treatment_name' && typeof value === 'string' && value.trim()) {
        transformedData[key] = value
          .split(',')
          .map((part: string) => part.trim())
          .filter(Boolean);
      }

      // Transform power duration field from comma-separated string to array
      if (
        key === 'power_duration' &&
        typeof value === 'string' &&
        value.includes(',')
      ) {
        transformedData[key] = value
          .split(',')
          .map((part: string) => part.trim())
          .filter(Boolean);
      }

      // Transform prescription medicines to ensure proper row states
      if (key === 'prescription_medicines' && Array.isArray(value)) {
        transformedData[key] = value.map((medicine: any, index: number) => ({
          ...medicine,
          id: medicine.id || `medicine_${index}_${Date.now()}`,
          _action: medicine._action || '', // Clean state for existing medicines
          _originalData: medicine._originalData || { ...medicine },
        }));
      }
    });

    return transformedData;
  };

  // Set form values when selectedRecord is available and form is ready
  React.useEffect(() => {
    if (selectedRecord && form && visible && !formLoading) {
      // Use setTimeout to ensure form is fully initialized
      setTimeout(() => {
        const transformedData = transformForEditing(selectedRecord);
        form?.setFieldsValue(transformedData);
      }, 100);
    }
  }, [selectedRecord, form, visible, formLoading]);

  // Early return if form should not be rendered
  if (!shouldRenderForm) {
    return null;
  }

  // Additional safety check to ensure form instance exists
  if (!form) {
    return null;
  }

  return (
    <CommonForm
      fields={editForm.fields}
      title={editForm.title}
      submitText={editForm.submitText}
      initialValues={editForm.initialValues}
      formDisplayMode={formDisplayMode}
      open={visible}
      drawerWidth={drawerWidth}
      modalWidth={modalWidth}
      form={form}
      formProps={editFormProps}
      saveButtonProps={editSaveButtonProps}
      formLoading={formLoading}
      onClose={onClose}
      onFinish={editFormProps.onFinish}
      resourceName={api.resource}
      loadingText="Loading form..."
      enableChat={enableChat}
      chatType={chatType}
      chatRelativeId={chatRelativeId}
      chatPatientId={chatPatientId}
      chatTitle={chatTitle}
      chatPlaceholder={chatPlaceholder}
      customTitle={customTitle}
      formSecondaryTab={editForm?.formSecondaryTab}
      isintialScroll={isintialScroll}
    />
  );
};
